import { isEqual } from 'lodash';
import { Reducer } from 'redux';
import { persistReducer, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import {
  AUTOS_SEARCH_PARAMETERS,
  AUTOS_SEARCH_REQUEST,
  AUTOS_SEARCH_SUCCESSFUL,
  AUTOS_SEARCH_FAILURE,
  AUTOS_RESULT_SUCCESSFUL,
  AUTOS_RESULT_REQUEST,
  AUTOS_RESULT_FAILURE,
  AUTOS_CLEAR,
  AUTOS_RESULT_CLEAR,
  AUTOS_TAB_FILTER,
  AUTOS_FILTER_CLEAR,
  AUTOS_SET_TRAVELER,
  AUTOS_AGENCIES_REQUEST,
  AUTOS_AGENCIES_SUCCESSFUL,
  AUTOS_AGENCIES_FAILURE,
  AUTOS_AGENCIES_CLEAR,
  AUTOS_DETAILS_REQUEST,
  AUTOS_DETAILS_SUCCESSFUL,
  AUTOS_VALIDATE_REQUEST,
  AUTOS_VALIDATE_SUCCESSFUL,
} from 'action-creators/autos';
import { RESET } from 'action-creators/generic';
import { VehicleOption } from 'models/auto/vehicle';
import { VehicleDetailResponse } from 'models/auto/vehicleDetail';
import { VehicleFilter } from 'models/auto/vehicleFilter';
import { VehicleSorterTypes, VehicleResultsResponse } from 'models/auto/vehicleResults';
import { VehicleResultsFilters } from 'models/auto/vehicleResults';
import { VehicleSearchResponse, VehicleSearchPayload } from 'models/auto/vehicleSearch';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { VehicleValidateResponse } from 'models/auto/vehicleValidate';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { TravelArrangerUsersAllowed } from 'services/userService';
export interface AutosState {
  pickUpPoint?: VehiclePlace;
  dropOffPoint?: VehiclePlace;
  pickUpDate?: string;
  dropOffDate?: string;
  pickUpTime?: string;
  dropOffTime?: string;
  lastTime?: string;
  traveler?: TravelArrangerUsersAllowed;
  diffDropOff: boolean;
  isLoadingSearch: boolean;
  isLoadingResults: boolean;
  isLoadingAgencies: boolean;
  isEnd: boolean;
  orderCriteria: VehicleSorterTypes;
  orderDesc: boolean;
  filters?: VehicleResultsFilters;
  autoTabFilter?: VehicleFilter;
  search?: VehicleSearchResponse;
  results?: VehicleOption[];
  agencies?: VehicleResultsResponse;
  request?: VehicleSearchPayload;
  error?: string;
  details?: {
    searchId?: string;
    optionId?: number;
    response?: VehicleDetailResponse;
  };
  validate?: {
    detailId?: string;
    response?: VehicleValidateResponse;
  };
  branchCompany?: GetAllBillingCompanyResponse;
}

const initialState: AutosState = {
  diffDropOff: false,
  isLoadingSearch: false,
  isLoadingResults: false,
  isLoadingAgencies: false,
  isEnd: false,
  orderCriteria: VehicleSorterTypes.Recommended,
  orderDesc: false,
};

const reducer: Reducer<AutosState> = (state = initialState, action) => {
  switch (action.type) {
    case AUTOS_TAB_FILTER:
      return {
        ...state,
        autoTabFilter: action.payload,
        filters: undefined,
      };
    case AUTOS_SEARCH_PARAMETERS:
      return {
        ...state,
        isLoadingSearch: false,
        isEnd: false,
        filters: undefined,
        search: undefined,
        results: undefined,
        ...action.payload,
      };
    case AUTOS_SEARCH_REQUEST:
      return {
        ...state,
        isLoadingSearch: true,
        isEnd: false,
        filters: undefined,
        search: undefined,
        results: undefined,
        ...action.payload,
      };
    case AUTOS_SEARCH_SUCCESSFUL:
      return {
        ...state,
        isLoadingSearch: false,
        search: {
          ...action.payload,
        },
        results: undefined,
        filters: undefined,
        lastTime: action.payload.resultsAvailableUntil,
      };
    case AUTOS_SEARCH_FAILURE:
      return {
        ...state,
        error: action.payload,
        search: undefined,
        isLoadingSearch: false,
      };
    case AUTOS_RESULT_REQUEST:
      return {
        ...state,
        isLoadingResults: true,
        isEnd: false,
        orderCriteria:
          action.payload.filters && action.payload.filters.OrderCriteria
            ? action.payload.filters.OrderCriteria
            : state.orderCriteria,
        orderDesc:
          action.payload.filters && typeof action.payload.filters.orderDesc !== 'undefined'
            ? action.payload.filters.orderDesc
            : state.orderDesc,
        filters: action.payload.filters || state.filters,
        search: {
          ...state.search,
        },
        results: !action.payload.filters || isEqual(state.filters, action.payload.filters) ? state.results : [],
      };
    case AUTOS_RESULT_SUCCESSFUL:
      return {
        ...state,
        isLoadingResults: false,
        isEnd: action.payload.vehicleOptions.length === 0,
        search: state.search,
        results: [...(state.results || []), ...action.payload.vehicleOptions],
      };
    case AUTOS_RESULT_FAILURE:
      return {
        ...state,
        error: action.payload,
        isLoadingResults: false,
      };
    case AUTOS_RESULT_CLEAR:
      return {
        ...state,
        results: undefined,
      };
    case AUTOS_AGENCIES_REQUEST:
      return {
        ...state,
        isLoadingAgencies: true,
        agencies: undefined,
      };
    case AUTOS_AGENCIES_SUCCESSFUL:
      return {
        ...state,
        isLoadingAgencies: false,
        agencies: action.payload,
      };
    case AUTOS_AGENCIES_FAILURE:
      return {
        ...state,
        error: action.payload,
        isLoadingAgencies: false,
      };
    case AUTOS_AGENCIES_CLEAR:
      return {
        ...state,
        agencies: undefined,
      };
    case AUTOS_FILTER_CLEAR:
      return {
        ...state,
        filters: undefined,
      };
    case AUTOS_CLEAR:
      return {
        ...state,
        search: undefined,
        pickUpPoint: undefined,
        dropOffPoint: undefined,
        pickUpDate: undefined,
        dropOffDate: undefined,
        pickUpTime: undefined,
        dropOffTime: undefined,
        diffDropOff: false,
        results: undefined,
        branchCompany: undefined,
      };
    case AUTOS_SET_TRAVELER:
      return {
        ...state,
        traveler: action.payload,
      };
    case AUTOS_DETAILS_REQUEST:
      return {
        ...state,
        details: {
          ...action.payload,
          response: undefined,
        },
      };
    case AUTOS_DETAILS_SUCCESSFUL:
      return {
        ...state,
        details: {
          ...state.details,
          response: action.payload,
        },
      };
    case AUTOS_VALIDATE_REQUEST:
      return {
        ...state,
        validate: {
          detailId: action.payload,
          response: undefined,
        },
      };
    case AUTOS_VALIDATE_SUCCESSFUL:
      return {
        ...state,
        validate: {
          ...state.validate,
          response: action.payload,
        },
      };
    case RESET:
      return initialState;
    default:
      return state;
  }
};

const migrations = {
  2: (state: any) => ({
    ...state,
    cabin: 'Economy',
  }),
};

const persistConfig = {
  storage,
  version: 2,
  key: 'autos',
  blacklist: ['isLoadingResults', 'isLoadingSearch', 'isLoadingAgencies', 'error'],
  migrate: createMigrate(migrations),
};

export default persistReducer(persistConfig, reducer);
